import { TubesAndPipesSection } from "src/app/shared/models/data/documentTemplate";
import { EnquiryHeaderItem } from "src/app/shared/models/data/enquiry.model";
import { RoundDigit } from "src/app/shared/models/data/roundDigit.model";
import { UserLogged } from "src/app/shared/models/data/userLogged.model";
import { withAssetVersion } from "src/app/shared/utils/asset-version";

export const TUBES_AND_PIPES = "Tubes and Pipes";
export const HOLLOW_BARS = "Hollow Bars";

async function loadRoundDigits(): Promise<any> {
    try {
        const response = await fetch(withAssetVersion('assets/mock/roundDigits.json'));
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error loading JSON:', error);
        return null;
    }
}

function getLanguage() {
    const userLogged = localStorage.getItem("userLogged");
    if (!userLogged)
        return 'en';
    else {
        const ul = JSON.parse(userLogged) as UserLogged
        return ul.language;
    }
}

function normalizeLanguage(language?: string): string {
    return (language || 'en').toLowerCase().split('-')[0];
}

function getAlloySurchargeText(isIncluded: boolean, language?: string): string {
    const lang = normalizeLanguage(language);
    const labels: Record<string, { included: string; notIncluded: string }> = {
        en: { included: 'Included', notIncluded: 'Not Included' },
        it: { included: 'Incluso', notIncluded: 'Non incluso' },
        fr: { included: 'Inclus', notIncluded: 'Non inclus' },
        de: { included: 'Inklusive', notIncluded: 'Nicht inklusive' }
    };
    const selected = labels[lang] || labels['en'];
    return isIncluded ? selected.included : selected.notIncluded;
}

function renderAlloySurcharge(row: any): string {
    const offerSchemeCode = row?.offerScheme?.offerSchemeCode;
    if (!offerSchemeCode) {
        return '';
    }

    const languageForRender = row?.__quotationLanguage || getLanguage();
    if (offerSchemeCode === 'FixAndFirm') {
        return getAlloySurchargeText(true, languageForRender);
    }
    if (offerSchemeCode === 'BasePricePlusAs') {
        return getAlloySurchargeText(false, languageForRender);
    }

    return '';
}

function formatDate(date: number): string {
    const language = getLanguage();
    const locale = language === 'en' ? 'en-US' : 'fr-FR'; // 'fr-FR' ensures dd/MM/yyyy format

    const options: Intl.DateTimeFormatOptions = language === 'en'
        ? { month: '2-digit', day: '2-digit', year: 'numeric' }  // MM/dd/yyyy
        : { day: '2-digit', month: '2-digit', year: 'numeric' }; // dd/MM/yyyy

    return new Date(date).toLocaleDateString(locale, options);
}


export async function getTubesAndPipesSection(salesOfficeCountry: string): Promise<TubesAndPipesSection> {
    const roundDigits = await loadRoundDigits() as RoundDigit[];

    const round = (item: EnquiryHeaderItem, fieldName: string, culture: string = 'it-IT'): string => {
        if (!item || !(fieldName in item)) {
            return '';
        }

        const isImperial = item.unitSystem === 'Imperial';
        const roundDigit = roundDigits.find(digit => digit.fieldName === fieldName && digit.section === 'Quotation');

        if (roundDigit) {
            const precision = isImperial ? roundDigit.imperialDigits : roundDigit.metricDigits;
            const value = item[fieldName] as number;

            if (value !== undefined && value !== null) {
                const loc = new Intl.Locale(culture ?? 'en-UK', { numberingSystem: 'latn' });
                const nf = new Intl.NumberFormat(loc, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision
                });
                return nf.format(value).replace(/\u202F/g, '\u00A0'); // narrow NBSP -> NBSP
            }
            else
                return '';
        }

        return item[fieldName];
    };

    let fields = [
        {
            label: 'customerItemNo',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: false,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US']
        },
        {
            label: 'rawMaterialGrade.rawMaterialGradeCode',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any) => { return row.normGrades && row.normGrades.length ? (row.normGrades as any[]).map(x => x.normGradeCode).join(', ') : ''; }
        },
        {
            label: 'applicableNorms',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
        },
        {
            label: 'ch',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any) => { return row?.sms?.marketingRoute.includes("Cold") ? 'C' : 'H' }
        },
        {
            label: 'mill.shortName',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            visibleIn: ['FR', 'IT', 'US'],
        },
        {
            label: 'quantity',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return round(row, 'quantity', culture) + ' ' + row.quantityUom }
        },
        {
            label: 'od',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.od ? round(row, 'od', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Od'] + ' ' + (row.odUom || ''); }
        },
        {
            label: 'wt',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.wt ? round(row, 'wt', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Wt'] + ' ' + (row.wtUom || ''); }
        },

        {
            label: 'id',
            type: 'number',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.id ? round(row, 'id', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Id'] + ' ' + (row.idUom || ''); }
        },
        {
            label: 'npsSchedule.npsDescription',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true,
            visibleIn: ['FR', 'IT', 'US']
        },
        {
            label: 'typeOfTolerance',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
        },
        {
            label: 'length',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return !row.uBent ? (row.lengthType == 'FL' ? round(row, 'lengthNominal', culture) : round(row, 'lengthMin', culture) + '-' + round(row, 'lengthMax', culture)) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Length'] + ' ' + row.lengthUom; }
        },
        {
            label: 'avgDevelopedLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any) => { return row.uBent ? row.lengthNominal.toString() : '' }
        },
        {
            label: 'weightPerLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['GE'],
            render: (row: any, _g?: any, culture?: string) => { return row.weightPerLength ? round(row, 'weightPerLength', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.weightPerLengthUom; }
        },
        {
            label: 'totalWeight',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.totalWeightStd ? round(row, 'totalWeightStd', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalWeight'] + ' ' + row.totalWeightStdUom; }

        },
        {
            label: 'totalLength',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.totalLength ? round(row, 'totalLength', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalLength'] + ' ' + row.totalLengthUom }
        },
        {
            label: 'priceLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.finalPricePerLength ? round(row, 'finalPricePerLength', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.finalPricePerLengthUom; }

        },
        {
            label: 'priceWeight',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.finalPrice ? round(row, 'finalPrice', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.costsUom; }

        },
        {
            label: 'totalPrice',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'IT', 'US'],
            render: ((row: any, _g?: any, culture?: string) => { return row.totalPrice ? round(row, 'totalPrice', culture).toString() : '' }),
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalPrice'] + ' ' + row.totalPriceUom; }
        },
        {
            label: 'alloySurcharge',
            type: 'string',
            value: '',
            editable: false,
            required: false,
            hideable: true,
            hidden: true,
            render: (row: any) => { return renderAlloySurcharge(row); }
        },
        {
            label: 'As',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.alloySurchargeValue ? round(row, 'alloySurchargeValue', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['As'] + ' ' + row.costsUom; }
        },
        {
            label: 'Es',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.energySurchargeValue ? round(row, 'energySurchargeValue', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Es'] + ' ' + row.costsUom; }
        },
        {
            label: 'deliveryDate',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: EnquiryHeaderItem, getFormattedDate: (date: number) => string) => {
                if (!row.deliveryDatesCollection || row.deliveryDatesCollection.length === 0) {
                    return '';
                }

                const isItdcr = false; //row.deliverySource?.toLowerCase() === 'itdcr'; MP: always false as per new requirements
                const singleDate = row.deliveryDatesCollection.length === 1;

                return row.deliveryDatesCollection
                    .map(dd => {
                        const formattedDate = isItdcr
                            ? (dd.deliveryWeekOfYearWithExtraLeadTime || '')
                            : (getFormattedDate
                                ? getFormattedDate(dd.deliveryNumberOfWeeksWithExtraLeadTime!)
                                : formatDate(dd.deliveryNumberOfWeeksWithExtraLeadTime!));

                        return singleDate
                            ? formattedDate
                            : `${formattedDate}: ${dd.deliveryQuantity} ${dd.deliveryQuantityUom}`;
                    })
                    .join(',\n');
            }
        },
        {
            label: 'deliveryNotes',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        },
        {
            label: 'remark',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        }
    ];

    fields.forEach(field => {
        if (field.visibleIn && field.visibleIn.includes(salesOfficeCountry)) {
            field.hidden = false;
        }
    });

    return {
        showTotalLength: false,
        showTotalPrice: false,
        showTotalWeight: false,
        technicalDescription: {
            isModified: false,
            selectedValues: {},
            value: ''
        },
        fields: fields,
        freeText: '',
        sectionName: TUBES_AND_PIPES,
        items: []
    }
}

export async function getHollowBarsSection(salesOfficeCountry: string): Promise<TubesAndPipesSection> {
    const roundDigits = await loadRoundDigits() as RoundDigit[];

    const round = (item: EnquiryHeaderItem, fieldName: string, culture: string = 'it-IT'): string => {
        if (!item || !(fieldName in item)) {
            return '';
        }

        const isImperial = item.unitSystem === 'Imperial';
        const roundDigit = roundDigits.find(digit => digit.fieldName === fieldName && digit.section === 'Quotation');

        if (roundDigit) {
            const precision = isImperial ? roundDigit.imperialDigits : roundDigit.metricDigits;
            const value = item[fieldName] as number;

            if (value !== undefined && value !== null) {
                const loc = new Intl.Locale(culture ?? 'en-UK', { numberingSystem: 'latn' });
                const nf = new Intl.NumberFormat(loc, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision
                });
                return nf.format(value).replace(/\u202F/g, '\u00A0'); // narrow NBSP -> NBSP
            }
            else
                return '';
        }

        return item[fieldName];
    };

    let fields = [
        {
            label: 'customerItemNo',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: false,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US']
        },
        {
            label: 'rawMaterialGrade.rawMaterialGradeCode',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any) => { return row.normGrades && row.normGrades.length ? (row.normGrades as any[]).map(x => x.normGradeCode).join(', ') : ''; }
        },
        {
            label: 'applicableNorms',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
        },
        {
            label: 'ch',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any) => { return row?.sms?.marketingRoute.includes("Cold") ? 'C' : 'H' }
        },
        {
            label: 'mill.shortName',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
        },
        {
            label: 'quantity',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return round(row, 'quantity', culture) + ' ' + row.quantityUom }
        },
        {
            label: 'od',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.od ? round(row, 'od', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Od'] + ' ' + (row.odUom || ''); }
        },
        {
            label: 'wt',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.wt ? round(row, 'wt', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Wt'] + ' ' + (row.wtUom || ''); }
        },

        {
            label: 'id',
            type: 'number',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.id ? round(row, 'id', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Id'] + ' ' + (row.idUom || ''); }
        },
        {
            label: 'npsSchedule.npsDescription',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        },
        {
            label: 'typeOfTolerance',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        },
        {
            label: 'length',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE'],
            render: (row: any, _g?: any, culture?: string) => { return !row.uBent ? (row.lengthType == 'FL' ? round(row, 'lengthNominal', culture) : round(row, 'lengthMin', culture) + '-' + round(row, 'lengthMax', culture)) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Length'] + ' ' + row.lengthUom; }
        },
        {
            label: 'avgDevelopedLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any) => { return row.uBent ? row.lengthNominal.toString() : '' }
        },
        {
            label: 'weightPerLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE'],
            render: (row: any, _g?: any, culture?: string) => { return row.weightPerLength ? round(row, 'weightPerLength', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.weightPerLengthUom; }
        },
        {
            label: 'totalWeight',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE'],
            render: (row: any, _g?: any, culture?: string) => { return row.totalWeightStd ? round(row, 'totalWeightStd', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalWeight'] + ' ' + row.totalWeightStdUom; }

        },
        {
            label: 'totalLength',
            type: 'number',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            render: (row: any, _g?: any, culture?: string) => { return row.totalLength ? round(row, 'totalLength', culture) : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalLength'] + ' ' + row.totalLengthUom }
        },
        {
            label: 'priceLength',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.finalPricePerLength ? round(row, 'finalPricePerLength', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.finalPricePerLengthUom; }

        },
        {
            label: 'priceWeight',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: any, _g?: any, culture?: string) => { return row.finalPrice ? round(row, 'finalPrice', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return row.costsUom; }

        },
        {
            label: 'totalPrice',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            render: (row: any, _g?: any, culture?: string) => { return row.totalPrice ? round(row, 'totalPrice', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['TotalPrice'] + ' ' + row.totalPriceUom; }
        },
        {
            label: 'alloySurcharge',
            type: 'string',
            value: '',
            editable: false,
            required: false,
            hideable: true,
            hidden: true,
            render: (row: any) => { return renderAlloySurcharge(row); }
        },
        {
            label: 'As',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.alloySurchargeValue ? round(row, 'alloySurchargeValue', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['As'] + ' ' + row.costsUom; }
        },
        {
            label: 'Es',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: true,
            render: (row: any, _g?: any, culture?: string) => { return row.energySurchargeValue ? round(row, 'energySurchargeValue', culture).toString() : '' },
            renderCustomLabel: (row: any, translations: any) => { return translations['quotation']['Es'] + ' ' + row.costsUom; }
        },
        {
            label: 'deliveryDate',
            type: 'string',
            value: '',
            editable: true,
            required: true,
            hideable: true,
            hidden: false,
            visibleIn: ['FR', 'GE', 'IT', 'US'],
            render: (row: EnquiryHeaderItem, getFormattedDate: (date: number) => string) => {
                if (!row.deliveryDatesCollection || row.deliveryDatesCollection.length === 0) {
                    return '';
                }

                const isItdcr = false; //row.deliverySource?.toLowerCase() === 'itdcr'; MP: always false as per new requirements
                const singleDate = row.deliveryDatesCollection.length === 1;

                return row.deliveryDatesCollection
                    .map(dd => {
                        const formattedDate = isItdcr
                            ? (dd.deliveryWeekOfYearWithExtraLeadTime || '')
                            : (getFormattedDate
                                ? getFormattedDate(dd.deliveryNumberOfWeeksWithExtraLeadTime!)
                                : formatDate(dd.deliveryNumberOfWeeksWithExtraLeadTime!));

                        return singleDate
                            ? formattedDate
                            : `${formattedDate}: ${dd.deliveryQuantity} ${dd.deliveryQuantityUom}`;
                    })
                    .join(',\n');
            }
        },
        {
            label: 'deliveryNotes',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        },
        {
            label: 'remark',
            type: 'string',
            value: '',
            editable: true,
            required: false,
            hideable: true,
            hidden: true
        }
    ];

    fields.forEach(field => {
        if (field.visibleIn && field.visibleIn.includes(salesOfficeCountry)) {
            field.hidden = false;
        }
    });


    return {
        showTotalLength: false,
        showTotalPrice: false,
        showTotalWeight: false,
        technicalDescription: {
            isModified: false,
            selectedValues: {},
            value: ''
        },
        fields: fields,
        freeText: '',
        sectionName: HOLLOW_BARS,
        items: []
    }
}


